// STT language choices exposed in the agent config dropdown.
// The selected `value` is stored on the agent/workflow config as `stt_language`
// and read by the agents runtime when it initializes the selected STT service.
// Grouped by language family so the (long) list stays scannable in a <select>.

export const DEFAULT_STT_LANGUAGE = 'hi';

export const STT_LANGUAGE_GROUPS = [
  {
    label: 'Common',
    options: [
      { value: 'multi', label: 'Multilingual (en, es, fr, de, hi, ru, pt, ja, it, nl)' },
      { value: 'hi', label: 'Hindi' },
      { value: 'en', label: 'English' },
      { value: 'en-IN', label: 'English (India)' },
      { value: 'en-US', label: 'English (US)' },
      { value: 'en-GB', label: 'English (UK)' },
    ],
  },
  {
    label: 'Indian languages',
    options: [
      { value: 'bn', label: 'Bengali' },
      { value: 'gu', label: 'Gujarati' },
      { value: 'gu-IN', label: 'Gujarati (India)' },
      { value: 'kn', label: 'Kannada' },
      { value: 'mr', label: 'Marathi' },
      { value: 'ta', label: 'Tamil' },
      { value: 'te', label: 'Telugu' },
      { value: 'ur', label: 'Urdu' },
    ],
  },
  {
    label: 'English variants',
    options: [
      { value: 'en-AU', label: 'English (Australia)' },
      { value: 'en-NZ', label: 'English (New Zealand)' },
    ],
  },
  {
    label: 'Arabic',
    options: [
      { value: 'ar-en', label: 'Arabic + English (Ar-En)' },
      { value: 'ar-au-en', label: 'Arabic + English (Audar)' },
    ],
  },
  {
    label: 'Chinese',
    options: [
      { value: 'zh', label: 'Chinese (Mandarin, Simplified)' },
      { value: 'zh-CN', label: 'Chinese (Mandarin, Simplified — CN)' },
      { value: 'zh-Hans', label: 'Chinese (Mandarin, Simplified — Hans)' },
      { value: 'zh-TW', label: 'Chinese (Mandarin, Traditional — TW)' },
      { value: 'zh-Hant', label: 'Chinese (Mandarin, Traditional — Hant)' },
      { value: 'zh-HK', label: 'Chinese (Cantonese, Traditional — HK)' },
    ],
  },
  {
    label: 'European',
    options: [
      { value: 'be', label: 'Belarusian' },
      { value: 'bs', label: 'Bosnian' },
      { value: 'bg', label: 'Bulgarian' },
      { value: 'ca', label: 'Catalan' },
      { value: 'hr', label: 'Croatian' },
      { value: 'cs', label: 'Czech' },
      { value: 'da', label: 'Danish' },
      { value: 'da-DK', label: 'Danish (Denmark)' },
      { value: 'nl', label: 'Dutch' },
      { value: 'nl-BE', label: 'Flemish (Dutch — Belgium)' },
      { value: 'et', label: 'Estonian' },
      { value: 'fi', label: 'Finnish' },
      { value: 'fr', label: 'French' },
      { value: 'fr-CA', label: 'French (Canada)' },
      { value: 'de', label: 'German' },
      { value: 'de-CH', label: 'German (Switzerland)' },
      { value: 'el', label: 'Greek' },
      { value: 'hu', label: 'Hungarian' },
      { value: 'it', label: 'Italian' },
      { value: 'lv', label: 'Latvian' },
      { value: 'lt', label: 'Lithuanian' },
      { value: 'mk', label: 'Macedonian' },
      { value: 'no', label: 'Norwegian' },
      { value: 'pl', label: 'Polish' },
      { value: 'pt', label: 'Portuguese' },
      { value: 'pt-BR', label: 'Portuguese (Brazil)' },
      { value: 'pt-PT', label: 'Portuguese (Portugal)' },
      { value: 'ro', label: 'Romanian' },
      { value: 'ru', label: 'Russian' },
      { value: 'sr', label: 'Serbian' },
      { value: 'sk', label: 'Slovak' },
      { value: 'sl', label: 'Slovenian' },
      { value: 'es', label: 'Spanish' },
      { value: 'es-419', label: 'Spanish (Latin America)' },
      { value: 'sv', label: 'Swedish' },
      { value: 'sv-SE', label: 'Swedish (Sweden)' },
      { value: 'uk', label: 'Ukrainian' },
    ],
  },
  {
    label: 'Other',
    options: [
      { value: 'he', label: 'Hebrew' },
      { value: 'id', label: 'Indonesian' },
      { value: 'ja', label: 'Japanese' },
      { value: 'ko', label: 'Korean' },
      { value: 'ko-KR', label: 'Korean (South Korea)' },
      { value: 'ms', label: 'Malay' },
      { value: 'fa', label: 'Persian' },
      { value: 'tl', label: 'Tagalog' },
      { value: 'th', label: 'Thai' },
      { value: 'th-TH', label: 'Thai (Thailand)' },
      { value: 'tr', label: 'Turkish' },
      { value: 'vi', label: 'Vietnamese' },
    ],
  },
];

// Flat set of valid codes, handy for validation / round-trip fallbacks.
export const STT_LANGUAGE_VALUES = STT_LANGUAGE_GROUPS.flatMap((group) =>
  group.options.map((option) => option.value)
);

export function isValidSttLanguage(value) {
  if (String(value || '').toLowerCase() === 'munsit-en-ar') return true;
  return STT_LANGUAGE_VALUES.includes(String(value || ''));
}

export function normalizeSttLanguage(value) {
  const code = String(value || '').trim();
  // Arabic now routes to the bilingual backend; legacy `munsit-en-ar` and
  // old Deepgram dialect codes all resolve to the single `ar-en` choice.
  const normalized = code.toLowerCase().replace(/_/g, '-');
  if (normalized === 'munsit-en-ar' || normalized === 'ar-en') return 'ar-en';
  // Audar's self-hosted backend has its own code; exempt it before the
  // generic ar-* catch-all below or it would collapse to Munsit's `ar-en`.
  if (normalized === 'ar-au-en') return 'ar-au-en';
  if (/^ar(?:[-_]|$)/i.test(code)) return 'ar-en';
  return isValidSttLanguage(code) ? code : DEFAULT_STT_LANGUAGE;
}
